/**
 * Student Abstract Exam — landing → (exam | review) → results.
 *
 * Two student-facing modes (set on start):
 *   - "exam"   : immediate feedback after each pick, live correct/wrong counter,
 *                pick is locked after answering, results screen at the end.
 *   - "review" : free practice — image first, "Show Answer" reveals correct +
 *                rationale, no score, free navigation.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useExitGuard } from "@/hooks/useExitGuard";
import ExitConfirmDialog from "@/components/ExitConfirmDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2, Sparkles, Brain, Trophy, ChevronLeft, ChevronRight, CheckCircle2,
  AlertCircle, Eye, BookOpen,
} from "lucide-react";
import { toast } from "sonner";

const CHOICES = ["A", "B", "C", "D"];

export default function StudentAbstractExam() {
  const [mode, setMode] = useState("landing"); // landing | exam | review | results
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);

  // Active session state
  const [sessionId, setSessionId] = useState(null);
  const [items, setItems] = useState([]);
  // Per-item state, keyed by item id:
  //   exam mode  -> { selected, locked, is_correct, correct_answer, rationale }
  //   review mode-> { revealed, correct_answer, rationale }
  const [itemState, setItemState] = useState({});
  const [idx, setIdx] = useState(0);
  const [imgCache, setImgCache] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const startedAtRef = useRef(null);

  // Results
  const [result, setResult] = useState(null);
  const [suspension, setSuspension] = useState(null); // { course_name, reason }

  useEffect(() => {
    api.get("/student/abstract-exam/status")
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ has_access: false }))
      .finally(() => setStatusLoading(false));
  }, []);

  const currentItem = items[idx];
  const inSession = mode === "exam" || mode === "review";

  // Block in-app nav, browser back, mobile swipe-back, tab close while a
  // session is running so a half-finished attempt isn't silently abandoned.
  // Uses the shared `useExitGuard` hook so behaviour matches the regular
  // course review / Smart Review pages.
  const { confirmOpen, confirmExit, cancelExit } = useExitGuard(inSession);

  // Lazy-load current + pre-fetch next image
  useEffect(() => {
    if (!inSession || !currentItem) return;
    const toFetch = [currentItem.id, items[idx + 1]?.id].filter((x) => x && !imgCache[x]);
    toFetch.forEach((iid) => {
      api.get(`/student/abstract-exam/items/${iid}/image`)
        .then((r) => setImgCache((c) => ({ ...c, [iid]: r.data.image_base64 })))
        .catch(() => { /* retry on next nav */ });
    });
  }, [inSession, idx, currentItem, items, imgCache]);

  const startSession = async (chosenMode) => {
    try {
      const r = await api.post("/student/abstract-exam/start", {});
      setSessionId(r.data.session_id);
      setItems(r.data.items);
      setItemState({});
      setIdx(0);
      setImgCache({});
      startedAtRef.current = Date.now();
      setMode(chosenMode);
    } catch (e) {
      // Special handling: 423 Locked = admin-suspended access. Surface the
      // reason via a full-screen suspension notice instead of a generic toast.
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 423 && detail && typeof detail === "object" && detail.code === "enrollment_suspended") {
        setSuspension({
          course_name: detail.course_name || "Abstract Exam",
          reason: detail.reason || "",
        });
        return;
      }
      toast.error((typeof detail === "string" ? detail : null) || "Could not start");
    }
  };

  // EXAM MODE — lock the answer immediately, get correct answer + rationale
  // back from the server so we can show feedback. Reuses /answer + a one-shot
  // result fetch via /submit only at the very end.
  const pickExamAnswer = async (letter) => {
    if (!currentItem || itemState[currentItem.id]?.locked) return;
    try {
      // Save the answer first (autosave)
      await api.post("/student/abstract-exam/answer", {
        session_id: sessionId, item_id: currentItem.id, selected: letter,
      });
      // Reveal correctness — we have a helper to fetch a single item's
      // answer using the existing image-auth path (item must belong to session).
      const r = await api.get(`/student/abstract-exam/items/${currentItem.id}/answer`);
      const correct = r.data.correct_answer;
      setItemState((s) => ({
        ...s,
        [currentItem.id]: {
          selected: letter, locked: true,
          is_correct: letter === correct,
          correct_answer: correct,
          rationale: r.data.rationale || "",
        },
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save answer");
    }
  };

  // REVIEW MODE — student clicks "Show Answer" to reveal
  const revealAnswer = async () => {
    if (!currentItem || itemState[currentItem.id]?.revealed) return;
    try {
      const r = await api.get(`/student/abstract-exam/items/${currentItem.id}/answer`);
      setItemState((s) => ({
        ...s,
        [currentItem.id]: {
          revealed: true,
          correct_answer: r.data.correct_answer,
          rationale: r.data.rationale || "",
        },
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load answer");
    }
  };

  const stats = useMemo(() => {
    let correct = 0, wrong = 0, answered = 0;
    Object.values(itemState).forEach((s) => {
      if (s?.locked) { answered += 1; s.is_correct ? correct++ : wrong++; }
    });
    return { correct, wrong, answered };
  }, [itemState]);

  const submitExam = async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/student/abstract-exam/submit", { session_id: sessionId });
      setResult(r.data);
      setMode("results");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally { setSubmitting(false); setConfirmSubmit(false); }
  };

  // ---------- Landing ----------
  if (mode === "landing") {
    return (
      <div className="space-y-6" data-testid="abstract-exam-landing">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-violet-600 dark:text-violet-400">Practice</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1 dark:text-slate-50">Abstract Reasoning</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Non-verbal patterns, sequences, matrices and visual logic — Civil Service Exam level.</p>
        </div>

        {statusLoading ? (
          <Card className="p-10 flex items-center justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></Card>
        ) : suspension ? (
          <Card className="p-8 sm:p-10 border-2 border-amber-300 bg-amber-50 text-center" data-testid="abstract-exam-suspended">
            <div className="mx-auto h-14 w-14 rounded-2xl bg-amber-100 grid place-items-center mb-3">
              <AlertCircle className="w-7 h-7 text-amber-600" />
            </div>
            <h2 className="font-display text-xl font-bold text-amber-900">Your access to {suspension.course_name} has been suspended</h2>
            {suspension.reason && (
              <p className="text-sm text-slate-800 mt-3 max-w-md mx-auto"><span className="font-semibold">Reason:</span> {suspension.reason}</p>
            )}
            <p className="text-sm text-slate-700 mt-3 max-w-md mx-auto">
              Please <a href="mailto:support@prepmasters.ph" className="underline text-blue-700 hover:text-blue-800 font-semibold">contact support</a> to restore access.
            </p>
          </Card>
        ) : !status?.has_access ? (
          <Card className="p-8 sm:p-10 border-dashed border-2 border-rose-200 dark:border-rose-800 bg-rose-50/60 dark:bg-rose-950/30 text-center" data-testid="no-access-card">
            <div className="mx-auto h-14 w-14 rounded-2xl bg-rose-100 dark:bg-rose-900 grid place-items-center mb-3">
              <AlertCircle className="w-7 h-7 text-rose-600 dark:text-rose-300" />
            </div>
            <h2 className="font-display text-xl font-bold text-slate-900 dark:text-slate-50">No Abstract Exam access yet</h2>
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-2 max-w-md mx-auto">An admin needs to assign you a question set first.</p>
          </Card>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 gap-4">
              <ModeCard
                icon={<Sparkles className="w-6 h-6 text-white" />}
                title="Exam Mode"
                subtitle="Timed-style practice with live scoring"
                description="Get instant feedback after each answer. Track correct vs wrong as you go. Lock in your picks — no going back."
                badge="Instant feedback"
                gradient="from-indigo-600 to-violet-600"
                bg="from-indigo-50 via-violet-50 to-white dark:from-indigo-950/40 dark:via-violet-950/30 dark:to-slate-900"
                onClick={() => startSession("exam")}
                testid="start-exam-mode-btn"
              />
              <ModeCard
                icon={<BookOpen className="w-6 h-6 text-white" />}
                title="Review Mode"
                subtitle="Study at your own pace"
                description="Look at each item, click Show Answer when ready, read the rationale. No score — just learning."
                badge="No pressure"
                gradient="from-emerald-600 to-teal-600"
                bg="from-emerald-50 via-teal-50 to-white dark:from-emerald-950/40 dark:via-teal-950/30 dark:to-slate-900"
                onClick={() => startSession("review")}
                testid="start-review-mode-btn"
              />
            </div>

            <Card className="p-5 border-slate-200" data-testid="last-score-card">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-md bg-emerald-50 dark:bg-emerald-950/40 grid place-items-center"><Trophy className="w-5 h-5 text-emerald-600" /></div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Your Last Score</div>
                    {status.last_session ? (
                      <div className="text-2xl font-display font-extrabold text-emerald-700 dark:text-emerald-300">
                        {Math.round(status.last_session.score_pct)}%
                        <span className="ml-2 text-sm text-slate-500 font-normal">{status.last_session.correct}/{status.last_session.count}</span>
                      </div>
                    ) : (
                      <div className="text-sm text-slate-500 mt-1">No sessions yet — take your first one!</div>
                    )}
                  </div>
                </div>
                <div className="text-right text-xs text-slate-500">
                  <div><span className="font-bold text-slate-700 dark:text-slate-300">{status.assigned_count}</span> items per session</div>
                  <div><span className="font-bold text-slate-700 dark:text-slate-300">{status.repo_total}</span> items in repository</div>
                </div>
              </div>
            </Card>
          </>
        )}
      </div>
    );
  }

  // ---------- Session (exam or review) ----------
  if (inSession && currentItem) {
    return (
      <>
        <SessionView
          mode={mode}
          items={items}
          idx={idx}
          setIdx={setIdx}
          currentItem={currentItem}
          itemState={itemState}
          imgCache={imgCache}
          stats={stats}
          pickExamAnswer={pickExamAnswer}
          revealAnswer={revealAnswer}
          onSubmit={() => setConfirmSubmit(true)}
          onExit={() => setMode("landing")}
          confirmSubmit={confirmSubmit}
          setConfirmSubmit={setConfirmSubmit}
          submitting={submitting}
          submitExam={submitExam}
        />
        <ExitConfirmDialog
          open={confirmOpen}
          mode="exam"
          onCancel={cancelExit}
          onConfirm={confirmExit}
        />
      </>
    );
  }

  // ---------- Results ----------
  if (mode === "results" && result) {
    return <ResultsView result={result} imgCache={imgCache} onRetake={() => setMode("landing")} />;
  }

  return null;
}

// ============================================================================
// Subcomponents
// ============================================================================

function ModeCard({ icon, title, subtitle, description, badge, gradient, bg, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`group text-left rounded-2xl p-6 border-2 border-slate-200 dark:border-slate-700 bg-gradient-to-br ${bg} transition-all hover:shadow-xl hover:-translate-y-0.5 active:scale-[0.99]`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${gradient} grid place-items-center shadow-md`}>{icon}</div>
        <span className="text-[10px] font-extrabold uppercase tracking-widest text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-900 px-2 py-1 rounded-full border border-slate-200 dark:border-slate-700">{badge}</span>
      </div>
      <h3 className="font-display text-xl font-extrabold dark:text-slate-50">{title}</h3>
      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{subtitle}</p>
      <p className="text-sm text-slate-600 dark:text-slate-300 mt-3 leading-relaxed">{description}</p>
      <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-slate-700 dark:text-slate-200 group-hover:translate-x-0.5 transition-transform">
        Start <ChevronRight className="w-4 h-4" />
      </div>
    </button>
  );
}

function SessionView({
  mode, items, idx, setIdx, currentItem, itemState, imgCache, stats,
  pickExamAnswer, revealAnswer, onSubmit,
  confirmSubmit, setConfirmSubmit, submitting, submitExam,
}) {
  const state = itemState[currentItem.id] || {};
  const isExam = mode === "exam";
  const progress = items.length ? Math.round((stats.answered / items.length) * 100) : 0;
  const reviewProgress = Object.values(itemState).filter((s) => s?.revealed).length;

  return (
    <div className="space-y-4 -mx-2 sm:mx-0" data-testid={`abstract-${mode}-running`}>
      {/* Top status bar — designed for mobile readability */}
      <div className="px-2 sm:px-0">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] font-extrabold uppercase tracking-widest text-violet-600 dark:text-violet-400">Item {idx + 1} of {items.length}</div>
            <h1 className="font-display text-xl sm:text-2xl font-bold tracking-tight dark:text-slate-50 leading-tight">{currentItem.question_type}</h1>
            <div className="flex gap-1.5 mt-1 flex-wrap">
              <Badge variant="outline" className="text-[10px]">{currentItem.bloom_level}</Badge>
              <Badge variant="outline" className="text-[10px]">{currentItem.difficulty}</Badge>
              <Badge className={isExam ? "bg-violet-100 text-violet-700 hover:bg-violet-100 text-[10px]" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]"}>
                {isExam ? "Exam Mode" : "Review Mode"}
              </Badge>
            </div>
          </div>
          {isExam ? (
            <div className="flex items-center gap-2" data-testid="exam-counters">
              <Counter label="Correct" value={stats.correct} color="emerald" />
              <Counter label="Wrong" value={stats.wrong} color="rose" />
            </div>
          ) : (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Reviewed</div>
              <div className="font-display text-2xl font-extrabold text-emerald-600 dark:text-emerald-300">{reviewProgress}/{items.length}</div>
            </div>
          )}
        </div>
        <div className="mt-2">
          <Progress value={isExam ? progress : Math.round((reviewProgress / items.length) * 100)} className="h-2" />
          <div className="text-[11px] text-slate-500 mt-1">{isExam ? `${stats.answered}/${items.length} answered` : `${reviewProgress}/${items.length} reviewed`}</div>
        </div>
      </div>

      {/* Instruction — bold, large, sits directly above the image */}
      {currentItem.instruction && (
        <div
          className="px-3 sm:px-0"
          data-testid="exam-item-instruction"
        >
          <p className="text-base sm:text-lg font-bold leading-snug text-slate-900 dark:text-slate-50">
            {currentItem.instruction}
          </p>
        </div>
      )}

      {/* Image card — large, near full-width on mobile */}
      <Card className="p-2 sm:p-3 border-slate-200 dark:border-slate-700 mx-0">
        <div className="relative w-full bg-white rounded-lg overflow-hidden flex items-center justify-center min-h-[50vh]">
          {imgCache[currentItem.id] ? (
            <img
              src={`data:image/png;base64,${imgCache[currentItem.id]}`}
              alt={`Item ${idx + 1}`}
              className="w-full h-auto max-h-[70vh] object-contain"
              data-testid="exam-item-image"
            />
          ) : (
            <Loader2 className="w-8 h-8 animate-spin text-slate-300" />
          )}
        </div>
      </Card>

      {/* Exam-mode feedback panel */}
      {isExam && state.locked && (
        <Card
          className={`mx-2 sm:mx-0 p-4 border-l-4 ${state.is_correct ? "border-l-emerald-500 bg-emerald-50 dark:bg-emerald-950/30" : "border-l-rose-500 bg-rose-50 dark:bg-rose-950/30"}`}
          data-testid="exam-feedback"
        >
          <div className="flex items-start gap-3">
            {state.is_correct
              ? <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
              : <AlertCircle className="w-6 h-6 text-rose-600 shrink-0" />}
            <div className="min-w-0">
              <div className={`font-display font-extrabold text-lg ${state.is_correct ? "text-emerald-700 dark:text-emerald-300" : "text-rose-700 dark:text-rose-300"}`}>
                {state.is_correct ? "Correct!" : "Incorrect."}
              </div>
              {!state.is_correct && (
                <div className="text-sm text-slate-700 dark:text-slate-200 mt-0.5">
                  Correct Answer: <span className="font-extrabold">{state.correct_answer}</span>
                </div>
              )}
              {state.rationale && (
                <div className="text-xs text-slate-600 dark:text-slate-300 mt-2 leading-relaxed">{state.rationale}</div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Choices — exam mode only */}
      {isExam && (
        <div className="grid grid-cols-2 gap-3 px-2 sm:px-0" data-testid="exam-choices">
          {CHOICES.map((letter) => {
            const isPicked = state.selected === letter;
            const isCorrect = state.locked && letter === state.correct_answer;
            const isWrongPick = state.locked && isPicked && !state.is_correct;
            const base = "h-16 sm:h-20 rounded-xl border-2 font-display text-2xl font-extrabold transition-all";
            let cls = `${base} border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:border-indigo-300 hover:bg-indigo-50/30 dark:hover:bg-indigo-950/20 active:scale-95`;
            if (state.locked) {
              if (isCorrect) cls = `${base} border-emerald-500 bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 shadow`;
              else if (isWrongPick) cls = `${base} border-rose-500 bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300`;
              else cls = `${base} border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 opacity-50`;
            } else if (isPicked) {
              cls = `${base} border-violet-500 bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300`;
            }
            return (
              <button
                key={letter}
                type="button"
                onClick={() => pickExamAnswer(letter)}
                disabled={state.locked}
                className={cls}
                data-testid={`exam-choice-${letter}`}
              >
                {letter}
              </button>
            );
          })}
        </div>
      )}

      {/* Review mode controls */}
      {!isExam && (
        <div className="px-2 sm:px-0 space-y-3" data-testid="review-controls">
          {!state.revealed ? (
            <Button
              onClick={revealAnswer}
              size="lg"
              className="w-full h-14 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold text-lg"
              data-testid="review-show-answer-btn"
            >
              <Eye className="w-5 h-5 mr-2" /> Show Answer
            </Button>
          ) : (
            <>
              <Card className="p-4 border-l-4 border-l-emerald-500 bg-emerald-50 dark:bg-emerald-950/30" data-testid="review-answer-card">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
                  <div>
                    <div className="text-xs uppercase tracking-widest font-bold text-emerald-700 dark:text-emerald-300">Correct Answer</div>
                    <div className="font-display text-3xl font-extrabold text-emerald-700 dark:text-emerald-300 leading-none">{state.correct_answer}</div>
                  </div>
                </div>
                {state.rationale && (
                  <div className="mt-3 pt-3 border-t border-emerald-200 dark:border-emerald-800">
                    <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold text-slate-600 dark:text-slate-300 mb-1.5"><Brain className="w-3.5 h-3.5" /> Rationale</div>
                    <div className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">{state.rationale}</div>
                  </div>
                )}
              </Card>
            </>
          )}
        </div>
      )}

      {/* Bottom nav */}
      <div className="px-2 sm:px-0 flex items-center justify-between gap-3 pt-2 pb-6">
        <Button variant="outline" disabled={idx === 0} onClick={() => setIdx((i) => Math.max(0, i - 1))} data-testid="exam-prev-btn">
          <ChevronLeft className="w-4 h-4 mr-1" /> Previous
        </Button>
        {idx < items.length - 1 ? (
          <Button
            onClick={() => setIdx((i) => Math.min(items.length - 1, i + 1))}
            className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white"
            data-testid="exam-next-btn"
          >
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : isExam ? (
          <Button
            onClick={onSubmit}
            className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold"
            data-testid="exam-submit-btn"
          >
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Submit Exam
          </Button>
        ) : (
          <Button
            onClick={onSubmit}
            variant="outline"
            data-testid="review-finish-btn"
          >
            Finish Review
          </Button>
        )}
      </div>

      {/* Submit confirm — exam mode only */}
      <Dialog open={confirmSubmit} onOpenChange={(o) => !o && !submitting && setConfirmSubmit(o)}>
        <DialogContent data-testid="exam-confirm-submit">
          <DialogHeader>
            <DialogTitle>{isExam ? "Submit exam?" : "Finish review?"}</DialogTitle>
            <DialogDescription>
              {isExam
                ? `You answered ${stats.answered} of ${items.length}. Correct: ${stats.correct}, Wrong: ${stats.wrong}.`
                : `You reviewed ${reviewProgress} of ${items.length} items. No score is recorded for Review Mode.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmSubmit(false)} disabled={submitting}>Keep going</Button>
            <Button onClick={submitExam} disabled={submitting} data-testid="exam-confirm-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              {isExam ? "Submit now" : "Finish"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Counter({ label, value, color }) {
  const colorMap = {
    emerald: "text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800",
    rose: "text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40 border-rose-200 dark:border-rose-800",
  };
  return (
    <div className={`px-3 py-1.5 rounded-lg border ${colorMap[color]}`} data-testid={`counter-${color}`}>
      <div className="text-[9px] font-extrabold uppercase tracking-widest leading-none">{label}</div>
      <div className="font-display text-2xl font-extrabold leading-none mt-0.5">{value}</div>
    </div>
  );
}

function ResultsView({ result, imgCache, onRetake }) {
  const [expanded, setExpanded] = useState(new Set());
  const toggle = (id) => setExpanded((s) => {
    const next = new Set(s); if (next.has(id)) next.delete(id); else next.add(id); return next;
  });
  const tier = useMemo(() => {
    const s = result.score_pct;
    if (s >= 90) return { label: "Excellent", color: "emerald" };
    if (s >= 75) return { label: "Strong", color: "indigo" };
    if (s >= 60) return { label: "Passing", color: "amber" };
    return { label: "Needs work", color: "rose" };
  }, [result.score_pct]);

  return (
    <div className="space-y-6" data-testid="abstract-exam-results">
      <Card className={`p-6 sm:p-8 border-${tier.color}-200 dark:border-${tier.color}-800 bg-gradient-to-br from-${tier.color}-50 to-white dark:from-${tier.color}-950/40 dark:to-slate-900`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Session complete</div>
            <h1 className="font-display text-4xl font-extrabold mt-1 dark:text-slate-50">{Math.round(result.score_pct)}%</h1>
            <div className="text-sm text-slate-600 dark:text-slate-300 mt-1">
              {result.correct}/{result.count} correct · {tier.label}
            </div>
          </div>
          <Button onClick={onRetake} className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white" data-testid="results-retake-btn">
            <Sparkles className="w-4 h-4 mr-2" /> Take another session
          </Button>
        </div>
      </Card>

      <div>
        <h2 className="font-display text-lg font-bold dark:text-slate-50 mb-2 flex items-center gap-2">
          <Brain className="w-5 h-5 text-indigo-600" /> Per-item review
        </h2>
        <div className="space-y-3">
          {result.review.map((r, i) => {
            const open = expanded.has(r.id);
            return (
              <Card
                key={r.id}
                className={`p-4 border-l-4 ${r.is_correct ? "border-l-emerald-500" : "border-l-rose-500"} cursor-pointer hover:shadow-md transition-shadow`}
                onClick={() => toggle(r.id)}
                data-testid={`results-item-${i}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs text-slate-400">#{i + 1}</span>
                      <span className="font-bold text-sm dark:text-slate-100">{r.question_type}</span>
                      {r.is_correct
                        ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Correct</Badge>
                        : <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">{r.selected ? "Wrong" : "Skipped"}</Badge>}
                      <Badge variant="outline" className="text-[10px]">{r.bloom_level}</Badge>
                      <Badge variant="outline" className="text-[10px]">{r.difficulty}</Badge>
                    </div>
                    <div className="text-xs text-slate-500 mt-1.5">
                      Your answer: <span className={`font-bold ${r.is_correct ? "text-emerald-700" : "text-rose-700"}`}>{r.selected || "—"}</span>
                      <span className="mx-2 text-slate-300">·</span>
                      Correct: <span className="font-bold text-indigo-700 dark:text-indigo-300">{r.correct_answer}</span>
                    </div>
                  </div>
                  <Eye className="w-4 h-4 text-slate-400 shrink-0" />
                </div>
                {open && (
                  <div className="mt-3 grid sm:grid-cols-2 gap-3">
                    <div className="bg-white dark:bg-slate-900 rounded-md p-2 border border-slate-200 dark:border-slate-700">
                      {imgCache[r.id]
                        ? <img src={`data:image/png;base64,${imgCache[r.id]}`} alt="" className="w-full" />
                        : <LazyResultImage iid={r.id} />}
                    </div>
                    <div className="p-3 rounded-md bg-indigo-50 dark:bg-indigo-950/30 text-sm text-slate-800 dark:text-slate-200">
                      <div className="font-semibold mb-1.5 flex items-center gap-1.5"><Brain className="w-4 h-4 text-indigo-600" /> Rationale</div>
                      {r.rationale || <span className="italic text-slate-400">No rationale recorded.</span>}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function LazyResultImage({ iid }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/student/abstract-exam/items/${iid}/image`)
      .then((r) => { if (alive) setSrc(r.data.image_base64); })
      .catch(() => { /* ignore */ });
    return () => { alive = false; };
  }, [iid]);
  return src
    ? <img src={`data:image/png;base64,${src}`} alt="" className="w-full" />
    : <div className="h-40 flex items-center justify-center text-slate-300"><Loader2 className="w-5 h-5 animate-spin" /></div>;
}
